import { Children } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdvancedTechnologyEdaVendor } from '@prisma/client';
import { UseMutationResult } from '@tanstack/react-query';
import { Alert, Button, ButtonProps, Form, FormItemProps, FormProps, ResultProps } from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import { AxiosResponse } from 'axios';

import { CreateAdvancedTechnologyEdaVendorDto } from '@app/domain-maps-interfaces';
import { Axios, FeatureFlags, OptionCardProps } from '@app/web-components';

import { ProcessPicker } from './inputs';

export type EdaVendorFormData = {
  supplier: DefaultOptionType;
  advancedTechnologyEdaVendorProcesses: DefaultOptionType[];
  edaVendor: boolean;
  t3Required: boolean;
};

export const useVendorMapForm = (
  mutate: UseMutationResult<
    AxiosResponse<AdvancedTechnologyEdaVendor, unknown>,
    unknown,
    CreateAdvancedTechnologyEdaVendorDto,
    unknown
  >,
) => {
  const [form] = Form.useForm<EdaVendorFormData>();
  const { isFeatureEnabled } = FeatureFlags.useFeatureFlags();
  const navigate = useNavigate();

  const getCancelButtonProps = (): ButtonProps => ({
    children: 'Cancel',
    onClick: () => navigate('../'),
  });

  const getCreateAnotherButtonProps = (): ButtonProps & React.RefAttributes<HTMLElement> => ({
    children: 'Create Another',
    key: 'create-another',
    onClick: () => {
      mutate.reset();
      form.resetFields();
    },
  });

  const getEDAVendorOptionCardProps = (): OptionCardProps => ({
    checkedHelp: 'Location validations will be skipped for this vendor.',
    uncheckedHelp: 'Location validations must be performed for this vendor.',
    children: <Form.Item label="EDA Vendor" name="edaVendor" />,
  });

  const getErrorFormItemProps = (): FormItemProps => ({
    children: <Alert message="Error" description={Axios.getErrorMessage(mutate.error)} type="error" showIcon />,
    colon: false,
    hidden: !mutate.error,
    label: ' ',
  });

  const getFormProps = (): FormProps => ({
    disabled: mutate.isPending || mutate.isSuccess,
    form,
    layout: 'vertical',
    style: { width: '100%', minWidth: 1000 },
    onFinish: ({ supplier, advancedTechnologyEdaVendorProcesses, edaVendor, t3Required }: EdaVendorFormData) =>
      mutate.mutate({
        globalUltimateSupplierId: typeof supplier.value === 'string' ? parseInt(supplier.value) : supplier.value ?? -1,
        processIds: advancedTechnologyEdaVendorProcesses.flatMap((process) =>
          process.value ? [typeof process.value === 'string' ? parseInt(process.value) : process.value] : [],
        ),
        edaVendor: !!edaVendor,
        t3Required: !!t3Required,
      }),
  });

  const getGotoVendorMapButtonProps = (): ButtonProps & React.RefAttributes<HTMLElement> => ({
    children: 'Go To EDA and CW Vendors mappings',
    key: 'go-to-landing',
    type: 'primary',
    onClick: () => navigate('../'),
  });

  const getProcessesFormItemProps = (): FormItemProps => ({
    children: <ProcessPicker.Select mode="multiple" placeholder="Processes to map to the supplier" />,
    label: 'Processes',
    name: 'advancedTechnologyEdaVendorProcesses',
    required: true,
    rules: [{ required: true, message: 'At least one process must be selected' }],
  });

  const getResetButtonProps = (): ButtonProps => ({
    children: 'Reset',
    onClick: () => form.resetFields(),
  });

  const getSaveButtonProps = (): ButtonProps => ({
    children: 'Save',
    loading: mutate.isPending,
    htmlType: 'submit',
    type: 'primary',
  });

  const getSuccessResultProps = (props?: ResultProps): ResultProps => ({
    ...props,
    extra: [<Button {...getGotoVendorMapButtonProps()} />, ...Children.toArray(props?.extra)],
    status: 'success',
  });

  const getSupplierFormItemProps = (): FormItemProps => ({
    label: 'Vendor',
    name: 'supplier',
  });

  const getT3RequiredOptionCardProps = (): OptionCardProps => ({
    checkedHelp: 'T3 validation will be performed for this vendor.',
    uncheckedHelp: 'T3 validation will be skipped for this vendor.',
    children: <Form.Item label="T3 Required" name="t3Required" />,
    hidden: !isFeatureEnabled('vendor_check_required'),
  });

  return {
    form,
    getCancelButtonProps,
    getCreateAnotherButtonProps,
    getEDAVendorOptionCardProps,
    getErrorFormItemProps,
    getFormProps,
    getProcessesFormItemProps,
    getResetButtonProps,
    getSaveButtonProps,
    getSuccessResultProps,
    getSupplierFormItemProps,
    getT3RequiredOptionCardProps,
  };
};
